import { noop } from 'utils/noop';
import { isNonEmptyArray } from 'utils/tsHelpers';
import { useCurrentProjectId } from 'utils/hooks/useCurrentProjectId';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useUserFieldsContext } from './userFields';
import { LOCAL_STORAGE_SEGMENTATION_FIELDS_KEY } from '../constants';
import { useGetUserFieldDisplayName } from '../hooks';

// Context for tracking columns to display in users table
type UsersTableColumns = {
  selectedColumns: string[];
  setSelectedColumns: (newColumns: string[]) => void;
};

export const defaultUsersTableColumnsState = {
  selectedColumns: [],
  setSelectedColumns: noop,
};

export const UsersTableColumnsContext = createContext<UsersTableColumns>(
  defaultUsersTableColumnsState,
);

export const useUsersTableColumnsContext = () => useContext(UsersTableColumnsContext);

export const UsersTableColumnsContextProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const projectId = useCurrentProjectId();
  const { getFieldDisplayName } = useGetUserFieldDisplayName();
  const { prioritizedUserFields = [], isLoading } = useUserFieldsContext();

  const defaultColumns = prioritizedUserFields.slice(0, 10);

  const [storedColumns, setStoredColumns] = useLocalStorage(
    `${LOCAL_STORAGE_SEGMENTATION_FIELDS_KEY}${projectId}`,
    defaultColumns, // This actually never gets used as the default since [] registers as truthy in useLocalStorage
  );

  const handleSetSelectedColumns = useCallback((newColumns: string[]) => {
    setSelectedColumns(newColumns);
    setStoredColumns(newColumns.map((col) => ({ title: getFieldDisplayName(col), field: col })));
  }, []);

  useEffect(() => {
    if (!isLoading && isNonEmptyArray(prioritizedUserFields)) {
      const parsedSelectedFields = (
        isNonEmptyArray(storedColumns) ? storedColumns : defaultColumns
      ).map(({ field }) => field);
      setSelectedColumns(parsedSelectedFields);
    }
  }, [prioritizedUserFields, isLoading]);

  const value = {
    selectedColumns,
    setSelectedColumns: handleSetSelectedColumns,
  };

  return (
    <UsersTableColumnsContext.Provider value={value}>{children}</UsersTableColumnsContext.Provider>
  );
};
